import { useEffect, useMemo, useRef, useState } from 'react'
import { CAT_SPRITE_MANIFEST } from '../pet-runtime/petAssets.js'
import { PetState, normalizePetRuntimeState } from '../pet-runtime/petStates.js'

const DEFAULT_SIZE = 128

function drawFallbackFrame(context, canvas, label) {
  const { height, width } = canvas
  context.clearRect(0, 0, width, height)
  context.fillStyle = '#f8fbff'
  context.fillRect(0, 0, width, height)
  context.fillStyle = '#dbeafe'
  context.beginPath()
  context.arc(width / 2, height / 2, width * 0.34, 0, Math.PI * 2)
  context.fill()
  context.fillStyle = '#1d4ed8'
  context.font = '600 14px system-ui, sans-serif'
  context.textAlign = 'center'
  context.fillText(label, width / 2, height - 18)
}

function PetAnimationController({
  className = '',
  manifest = CAT_SPRITE_MANIFEST,
  paused = false,
  size = DEFAULT_SIZE,
  state = PetState.IDLE,
}) {
  const canvasRef = useRef(null)
  const imageRef = useRef(null)
  const rafRef = useRef(0)
  const lastFrameAtRef = useRef(0)
  const frameRef = useRef(0)
  const [loadError, setLoadError] = useState(false)
  const normalizedState = normalizePetRuntimeState(state)
  const clip = manifest.states[normalizedState] || manifest.states[PetState.IDLE]

  const canvasSize = useMemo(() => ({
    height: clip?.frameHeight || size,
    width: clip?.frameWidth || size,
  }), [clip?.frameHeight, clip?.frameWidth, size])

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!canvas || !context || !clip?.src) return undefined

    let cancelled = false
    const image = new Image()
    imageRef.current = image
    frameRef.current = 0
    lastFrameAtRef.current = 0
    setLoadError(false)

    const drawFrame = (timestamp) => {
      if (cancelled) return
      if (!paused) {
        const frameDuration = 1000 / Math.max(1, clip.fps || 8)
        if (!lastFrameAtRef.current || timestamp - lastFrameAtRef.current >= frameDuration) {
          const nextFrame = frameRef.current
          context.clearRect(0, 0, canvas.width, canvas.height)
          context.drawImage(
            image,
            nextFrame * clip.frameWidth,
            0,
            clip.frameWidth,
            clip.frameHeight,
            0,
            0,
            canvas.width,
            canvas.height,
          )
          frameRef.current = (nextFrame + 1) % Math.max(1, clip.frames || 1)
          lastFrameAtRef.current = timestamp
        }
      }
      rafRef.current = window.requestAnimationFrame(drawFrame)
    }

    image.onload = () => {
      if (cancelled) return
      rafRef.current = window.requestAnimationFrame(drawFrame)
    }
    image.onerror = () => {
      if (cancelled) return
      setLoadError(true)
      drawFallbackFrame(context, canvas, clip.alt || normalizedState)
    }
    image.src = clip.src

    return () => {
      cancelled = true
      window.cancelAnimationFrame(rafRef.current)
    }
  }, [clip, normalizedState, paused])

  return (
    <canvas
      aria-hidden="true"
      className={[
        'pet-animation-controller',
        loadError ? 'is-missing-asset' : '',
        className,
      ].filter(Boolean).join(' ')}
      height={canvasSize.height}
      ref={canvasRef}
      width={canvasSize.width}
    />
  )
}

export default PetAnimationController
